import { useEffect, useMemo, useState } from 'react'
import { Alert, Modal, Select, Space, Spin, Switch, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useToast } from '@/contexts/ToastContext'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'
import {
  editableFeatureAccessUserTypeIds,
  getExclusiveCounterpartFeatureKey,
  getFeatureAccessFeatureLabel,
  type FeatureAccessGroupRow,
  type FeatureAccessUserGroupOption,
  type FeatureAccessUserTypeRow,
  getFeatureAccessPermissionLabel,
  hiddenFeatureAccessUserTypes,
  type ClientRow,
  type UpdateFeatureAccessRequest,
} from '../types'
import { useClientFeatureAccess, useUpdateClientFeatureAccessMutation } from '../hooks'

type FeatureAccessModalProps = {
  open: boolean
  client: ClientRow | null
  onClose: () => void
}

type PermissionTableRow = FeatureAccessUserTypeRow & {
  key: string
  featureKey: string
  featureName: string
}

type PermissionSection = {
  key: string
  permission: string
  featureKey: string
  featureName: string
  label: string
  rows: PermissionTableRow[]
  groupRows: FeatureAccessGroupRow[]
}

type PermissionOption = {
  key: string
  label: string
}

function buildRowKey(featureKey: string, permission: string, userTypeId: number | null, userGroupId: string | null) {
  const principalKey = userGroupId ? `group:${userGroupId}` : `type:${userTypeId ?? 'null'}`
  return `${featureKey}:${permission}:${principalKey}`
}

function buildPermissionKey(featureKey: string, permission: string) {
  return `${featureKey}:${permission}`
}

function buildEnabledMap(sections: PermissionSection[]) {
  return sections.reduce<Record<string, boolean>>((acc, section) => {
    section.rows.forEach((row) => {
      acc[row.key] = row.enabled
    })
    return acc
  }, {})
}

function buildSelectedGroupMap(sections: PermissionSection[]) {
  return sections.reduce<Record<string, string[]>>((acc, section) => {
    acc[section.key] = section.groupRows
      .filter((row) => row.userGroupId && row.enabled)
      .map((row) => row.userGroupId as string)
    return acc
  }, {})
}

/**
 * A feature counts as enabled for the client when any user type or any user group holds
 * one of its permissions, which is the same test the API applies when it rejects a save
 * that enables two mutually exclusive features.
 */
function getEnabledFeatureKeys(
  sections: PermissionSection[],
  enabledByKey: Record<string, boolean>,
  selectedGroupIdsByPermissionKey: Record<string, string[]>
) {
  return sections.reduce<Set<string>>((acc, section) => {
    const hasEnabledUserType = section.rows.some((row) => enabledByKey[row.key])
    const hasEnabledGroup = (selectedGroupIdsByPermissionKey[section.key] ?? []).length > 0
    if (hasEnabledUserType || hasEnabledGroup) {
      acc.add(section.featureKey)
    }
    return acc
  }, new Set<string>())
}

function normalizeUserGroupId(value: string | null | undefined) {
  return (value || '').trim().toLowerCase()
}

export function FeatureAccessModal({ open, client, onClose }: FeatureAccessModalProps) {
  const authSession = useAuthSession()
  const authorization = useAuthorization()
  const { addToast } = useToast()
  const clientId = client?.id || ''
  const query = useClientFeatureAccess(clientId, open && !!clientId)
  const updateMutation = useUpdateClientFeatureAccessMutation(clientId)
  const [enabledByKey, setEnabledByKey] = useState<Record<string, boolean>>({})
  const [selectedUserGroupIdsByPermissionKey, setSelectedUserGroupIdsByPermissionKey] = useState<Record<string, string[]>>({})
  const [selectedPermissionKey, setSelectedPermissionKey] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [exclusiveWarning, setExclusiveWarning] = useState<string | null>(null)

  const userGroupLabelById = useMemo(() => {
    return ((query.data?.metadata?.groups || []) as FeatureAccessUserGroupOption[]).reduce<Record<string, string>>((acc, group) => {
      acc[normalizeUserGroupId(group.groupId)] = group.name
      return acc
    }, {})
  }, [query.data?.metadata?.groups])

  const permissionSections = useMemo<PermissionSection[]>(() => {
    return (query.data?.features || []).flatMap((feature) =>
      (feature.permissions || []).map((permission) => ({
        ...permission,
        key: buildPermissionKey(feature.featureKey, permission.permission),
        featureKey: feature.featureKey,
        featureName: feature.featureName,
        label: getFeatureAccessPermissionLabel(feature.featureName, permission.permission),
        rows: (permission.rows || [])
          .filter((row) => (row.userTypeId === null ? true : !hiddenFeatureAccessUserTypes.has(row.userTypeName)))
          .map((row) => ({
            ...row,
            key: buildRowKey(feature.featureKey, permission.permission, row.userTypeId, null),
            featureKey: feature.featureKey,
            featureName: feature.featureName,
          })),
        groupRows: permission.groupRows || [],
      }))
    )
  }, [query.data, userGroupLabelById])

  const permissionOptions = useMemo<PermissionOption[]>(() => {
    return permissionSections.map((section) => ({
      key: section.key,
      label: section.label,
    }))
  }, [permissionSections])

  const selectedSection = useMemo(
    () => permissionSections.find((section) => section.key === selectedPermissionKey) ?? permissionSections[0] ?? null,
    [permissionSections, selectedPermissionKey]
  )

  const clientAdminRowKeyByPermission = useMemo(() => {
    return permissionSections.reduce<Record<string, string>>((acc, section) => {
      const row = section.rows.find((item) => item.userTypeId === UserTypes.ClientAdmin)
      if (row) {
        acc[section.key] = row.key
      }
      return acc
    }, {})
  }, [permissionSections])

  const advisorAdminRowKeyByPermission = useMemo(() => {
    return permissionSections.reduce<Record<string, string>>((acc, section) => {
      const row = section.rows.find((item) => item.userTypeId === UserTypes.AdminAdvisor)
      if (row) {
        acc[section.key] = row.key
      }
      return acc
    }, {})
  }, [permissionSections])

  const featureNameByKey = useMemo(() => {
    return permissionSections.reduce<Record<string, string>>((acc, section) => {
      acc[section.featureKey] = getFeatureAccessFeatureLabel(section.featureKey, section.featureName)
      return acc
    }, {})
  }, [permissionSections])

  const enabledFeatureKeys = useMemo(
    () => getEnabledFeatureKeys(permissionSections, enabledByKey, selectedUserGroupIdsByPermissionKey),
    [permissionSections, enabledByKey, selectedUserGroupIdsByPermissionKey]
  )

  /**
   * Returns the feature standing in the way of enabling `featureKey`, or null when nothing
   * blocks it. Enabling stays blocked until every row of the counterpart is turned off.
   */
  const getBlockingFeatureKey = (featureKey: string) => {
    const counterpartKey = getExclusiveCounterpartFeatureKey(featureKey)
    if (!counterpartKey || !enabledFeatureKeys.has(counterpartKey)) return null
    return counterpartKey
  }

  const warnExclusiveFeature = (featureKey: string, blockingFeatureKey: string) => {
    const message =
      `${featureNameByKey[featureKey] ?? featureKey} cannot be enabled while ` +
      `${featureNameByKey[blockingFeatureKey] ?? blockingFeatureKey} is enabled for this client. ` +
      'Turn the other one off first.'
    setSaveError(null)
    setExclusiveWarning(message)
    addToast(message, 'warning')
  }

  const isAdvisorAdminEnabledAllowed = (sectionKey: string, state: Record<string, boolean>) => {
    const clientAdminRowKey = clientAdminRowKeyByPermission[sectionKey]
    return clientAdminRowKey ? Boolean(state[clientAdminRowKey]) : false
  }

  useEffect(() => {
    if (!open || !query.data) return
    setEnabledByKey(buildEnabledMap(permissionSections))
    setSelectedUserGroupIdsByPermissionKey(buildSelectedGroupMap(permissionSections))
    setSaveError(null)
    setExclusiveWarning(null)
  }, [open, query.data, permissionSections])

  useEffect(() => {
    if (!open) return
    setSelectedPermissionKey((prev) =>
      prev && permissionOptions.some((option) => option.key === prev) ? prev : permissionOptions[0]?.key ?? null
    )
  }, [open, permissionOptions])

  useEffect(() => {
    if (open) return
    setEnabledByKey({})
    setSelectedUserGroupIdsByPermissionKey({})
    setSelectedPermissionKey(null)
    setSaveError(null)
    setExclusiveWarning(null)
  }, [open])

  const handleClose = () => {
    if (updateMutation.isPending) return
    onClose()
  }

  const buildPayload = (): UpdateFeatureAccessRequest => ({
    features: (query.data?.features || []).map((feature) => ({
      featureKey: feature.featureKey,
      permissions: (feature.permissions || []).map((permission) => {
        const sectionKey = buildPermissionKey(feature.featureKey, permission.permission)
        const selectedUserGroupIds = selectedUserGroupIdsByPermissionKey[sectionKey] ?? []

        return {
          permission: permission.permission,
          rows: permission.rows
            .filter(
              (row) => row.userTypeId !== null && editableFeatureAccessUserTypeIds.has(row.userTypeId)
            )
            .map((row) => {
              const rowKey = buildRowKey(feature.featureKey, permission.permission, row.userTypeId, null)
              const enabled =
                row.userTypeId === UserTypes.AdminAdvisor
                  ? isAdvisorAdminEnabledAllowed(sectionKey, enabledByKey) && Boolean(enabledByKey[rowKey])
                  : Boolean(enabledByKey[rowKey])

              return {
                userTypeId: row.userTypeId,
                enabled,
              }
            }),
          groupRows: selectedUserGroupIds.map((userGroupId) => ({
            userGroupId,
            enabled: true,
          })),
        }
      }),
    })),
  })

  const handleSave = async () => {
    if (!clientId || !query.data) return

    setSaveError(null)
    try {
      await updateMutation.mutateAsync(buildPayload())
      if (authSession?.currentClientId === clientId) {
        await authorization.refresh()
      }
      addToast('Feature access updated', 'success')
      onClose()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Unable to update feature access')
    }
  }

  const blockingFeatureKeyForSelectedSection = selectedSection ? getBlockingFeatureKey(selectedSection.featureKey) : null
  const queryError = query.error instanceof Error ? query.error.message : 'Unable to load feature access'
  const isLoading = query.isLoading
  const isError = query.isError
  const hasSections = permissionSections.length > 0
  const userGroupOptions = useMemo(
    () =>
      (query.data?.metadata?.groups || []).map((group: FeatureAccessUserGroupOption) => ({
        value: group.groupId,
        label: group.name,
      })),
    [query.data?.metadata?.groups]
  )

  const columns: ColumnsType<PermissionTableRow> = [
    {
      title: 'User Type',
      dataIndex: 'userTypeName',
      key: 'userTypeName',
      width: 260,
      render: (value: string) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      title: 'Enable / Disable',
      key: 'enabled',
      width: 180,
      render: (_: unknown, record: PermissionTableRow) => {
        const sectionKey = buildPermissionKey(record.featureKey, record.permission)
        const isAdvisorAdminBlocked =
          record.userTypeId === UserTypes.AdminAdvisor && !isAdvisorAdminEnabledAllowed(sectionKey, enabledByKey)
        const advisorAdminRowKey =
          record.userTypeId === UserTypes.ClientAdmin ? advisorAdminRowKeyByPermission[sectionKey] ?? null : null

        return (
          <Switch
            checked={Boolean(enabledByKey[record.key])}
            disabled={
              isLoading ||
              updateMutation.isPending ||
              record.userTypeId === null ||
              isAdvisorAdminBlocked
            }
            aria-label={`Toggle ${record.permission} for ${record.userTypeName}`}
            onChange={(checked) => {
              if (checked) {
                const blockingFeatureKey = getBlockingFeatureKey(record.featureKey)
                if (blockingFeatureKey) {
                  warnExclusiveFeature(record.featureKey, blockingFeatureKey)
                  return
                }
              }

              setEnabledByKey((prev) => ({
                ...prev,
                [record.key]: checked,
                ...(!checked && advisorAdminRowKey
                  ? {
                      [advisorAdminRowKey]: false,
                    }
                  : {}),
              }))
              setSaveError(null)
              setExclusiveWarning(null)
            }}
          />
        )
      },
    },
  ]

  return (
    <Modal
      open={open}
      title="Feature access"
      width={920}
      okText="Save"
      cancelText="Cancel"
      okButtonProps={{
        disabled: !query.data || isLoading || isError,
        loading: updateMutation.isPending,
      }}
      onOk={() => void handleSave()}
      onCancel={handleClose}
      destroyOnHidden
    >
      <Space direction="vertical" size={16} className="w-full">
        <div>
          <Typography.Text type="secondary">Client</Typography.Text>
          <Typography.Title level={5} className="!mt-1 !mb-0">
            {client?.name || 'Selected client'}
          </Typography.Title>
        </div>

        {isLoading && (
          <div className="py-10 text-center">
            <Spin />
          </div>
        )}

        {query.isError && <Alert type="error" showIcon message={queryError} />}
        {saveError && <Alert type="error" showIcon message={saveError} />}
        {exclusiveWarning && (
          <Alert
            type="warning"
            showIcon
            closable
            message={exclusiveWarning}
            onClose={() => setExclusiveWarning(null)}
          />
        )}

        {!isLoading && !isError && query.data && !hasSections && (
          <Typography.Text type="secondary">
            No editable feature permissions are available for this client.
          </Typography.Text>
        )}

        {!isLoading && !isError && hasSections && (
          <Space direction="vertical" size={16} className="w-full">
            <div>
              <Typography.Text type="secondary">Permission</Typography.Text>
              <Select
                className="mt-1 w-full"
                aria-label="Permission"
                value={selectedSection?.key}
                options={permissionOptions.map((option) => ({
                  value: option.key,
                  label: option.label,
                }))}
                onChange={(value) => {
                  setSelectedPermissionKey(value)
                  setSaveError(null)
                }}
              />
            </div>

            {selectedSection && (
              <>
                <div>
                  <Typography.Text type="secondary">User Group</Typography.Text>
                    <Select
                      mode="multiple"
                      allowClear
                      className="mt-1 w-full"
                      placeholder="Select user groups"
                      options={userGroupOptions}
                      value={selectedUserGroupIdsByPermissionKey[selectedSection.key] ?? []}
                      disabled={isLoading || updateMutation.isPending}
                      aria-label={`Select user groups for ${selectedSection.permission}`}
                      onChange={(value) => {
                        const nextValue = Array.isArray(value) ? value.map(String) : []
                        const isAdding = nextValue.length > (selectedUserGroupIdsByPermissionKey[selectedSection.key] ?? []).length
                        if (isAdding) {
                          const blockingFeatureKey = getBlockingFeatureKey(selectedSection.featureKey)
                          if (blockingFeatureKey) {
                            warnExclusiveFeature(selectedSection.featureKey, blockingFeatureKey)
                            return
                          }
                        }

                        setSelectedUserGroupIdsByPermissionKey((prev) => ({
                          ...prev,
                          [selectedSection.key]: nextValue,
                        }))
                        setSaveError(null)
                        setExclusiveWarning(null)
                      }}
                    />
                </div>

                {blockingFeatureKeyForSelectedSection && (
                  <Alert
                    type="info"
                    showIcon
                    message={`${featureNameByKey[selectedSection.featureKey] ?? selectedSection.featureName} is unavailable while ${
                      featureNameByKey[blockingFeatureKeyForSelectedSection] ?? blockingFeatureKeyForSelectedSection
                    } is enabled for this client. Only one of the two can be on at a time.`}
                  />
                )}

                <Table<PermissionTableRow>
                  rowKey="key"
                  columns={columns}
                  dataSource={selectedSection.rows}
                  pagination={false}
                  size="small"
                />
              </>
            )}
          </Space>
        )}
      </Space>
    </Modal>
  )
}
